/**
 * Classify attribution from `utmReferrer` when UTM source/medium are missing.
 *
 * This is a LOGIC-IDENTICAL MIRROR of the server-side classifier at
 * `spotme-server/src/api/marketing/classifyAttribution.ts`. The function
 * bodies and constant tables must match EXACTLY; file headers, imports,
 * and the inline `IAttribution` declaration (the server imports its type)
 * deliberately differ per repo. If you change any logic in one file,
 * change the other in the same PR.
 *
 * Why a frontend mirror exists: the server runs this at marketing-report
 * render time to derive source/medium from referrer + click IDs. PostHog,
 * however, only sees raw URL params + `document.referrer` on the client.
 * Without this mirror, PostHog cohorts that filter on `utm_source=facebook`
 * (paid) or `utm_medium=organic` are silently empty, because the raw
 * URL never carries those values — only `fbclid` / a Referer header does.
 *
 * Resolution order matches the server:
 *   1. UTMs already set → respect them (last-touch wins)
 *   2. Click IDs (gclid → google/cpc, fbclid → facebook/paid-social,
 *      msclkid → bing/cpc)
 *   3. Referrer domain (organic search, organic social, other referrals)
 *   4. Internal / direct fallbacks
 *
 * Pure function — no I/O, no React, safe to call from any layer.
 */

export interface IAttribution {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  utmPage?: string
  utmReferrer?: string
  utmLandingTimestamp?: string
  gclid?: string
  fbclid?: string
  msclkid?: string
}

type ReferrerRule = {
  match: RegExp
  source: string
  medium: string
}

/** Ordered list — first match wins. Keep narrow/specific patterns first. */
const REFERRER_RULES: ReferrerRule[] = [
  // AI assistants / answer engines. MUST stay above the search-engine rules:
  // `gemini.google.com` matches the `google\.` rule and `edgeservices.bing.com`
  // matches the `bing\.com$` rule, so ordering is what keeps AI traffic out of
  // the organic-search bucket. Referrers are stripped on most AI surfaces, so
  // what lands here is a floor, not the true channel size.
  { match: /(^|\.)chatgpt\.com$/i, source: 'chatgpt', medium: 'ai-assistant' },
  { match: /(^|\.)openai\.com$/i, source: 'chatgpt', medium: 'ai-assistant' },
  {
    match: /(^|\.)perplexity\.ai$/i,
    source: 'perplexity',
    medium: 'ai-assistant'
  },
  { match: /(^|\.)claude\.ai$/i, source: 'claude', medium: 'ai-assistant' },
  {
    match: /(^|\.)(gemini|bard)\.google\.com$/i,
    source: 'gemini',
    medium: 'ai-assistant'
  },
  {
    match: /(^|\.)copilot\.microsoft\.com$/i,
    source: 'copilot',
    medium: 'ai-assistant'
  },
  {
    match: /(^|\.)edgeservices\.bing\.com$/i,
    source: 'copilot',
    medium: 'ai-assistant'
  },
  {
    match: /(^|\.)deepseek\.com$/i,
    source: 'deepseek',
    medium: 'ai-assistant'
  },
  { match: /(^|\.)grok\.com$/i, source: 'grok', medium: 'ai-assistant' },
  { match: /(^|\.)x\.ai$/i, source: 'grok', medium: 'ai-assistant' },
  { match: /(^|\.)mistral\.ai$/i, source: 'mistral', medium: 'ai-assistant' },
  { match: /(^|\.)meta\.ai$/i, source: 'meta-ai', medium: 'ai-assistant' },
  { match: /(^|\.)you\.com$/i, source: 'you', medium: 'ai-assistant' },
  { match: /(^|\.)poe\.com$/i, source: 'poe', medium: 'ai-assistant' },
  { match: /(^|\.)duck\.ai$/i, source: 'duck-ai', medium: 'ai-assistant' },
  // Search engines
  { match: /(^|\.)google\./i, source: 'google', medium: 'organic' },
  { match: /(^|\.)bing\.com$/i, source: 'bing', medium: 'organic' },
  { match: /(^|\.)duckduckgo\.com$/i, source: 'duckduckgo', medium: 'organic' },
  { match: /(^|\.)yahoo\./i, source: 'yahoo', medium: 'organic' },
  { match: /(^|\.)yandex\./i, source: 'yandex', medium: 'organic' },
  { match: /(^|\.)baidu\.com$/i, source: 'baidu', medium: 'organic' },
  // Social (organic — paid social is tagged with UTMs)
  {
    match: /(^|\.)(facebook|fb)\.com$/i,
    source: 'facebook',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)(instagram)\.com$/i,
    source: 'instagram',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)l?\.?linkedin\.com$/i,
    source: 'linkedin',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)tiktok\.com$/i,
    source: 'tiktok',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)(twitter|t|x)\.com$/i,
    source: 'twitter',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)youtube\.com$/i,
    source: 'youtube',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)reddit\.com$/i,
    source: 'reddit',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)pinterest\.(com|mx)$/i,
    source: 'pinterest',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)whatsapp\.com$/i,
    source: 'whatsapp',
    medium: 'social-organic'
  },
  // Messaging
  { match: /(^|\.)t\.co$/i, source: 'twitter', medium: 'social-organic' },
  {
    match: /(^|\.)lm\.facebook\.com$/i,
    source: 'facebook',
    medium: 'social-organic'
  },
  {
    match: /(^|\.)m\.facebook\.com$/i,
    source: 'facebook',
    medium: 'social-organic'
  }
]

/** Parse hostname from an arbitrary referrer string. Returns null on junk. */
const parseHostname = (referrer: string): string | null => {
  try {
    const withProto = referrer.startsWith('http')
      ? referrer
      : `https://${referrer}`
    return new URL(withProto).hostname.toLowerCase()
  } catch {
    return null
  }
}

/**
 * Given an attribution blob, fill in `utmSource`/`utmMedium` when missing.
 * Returns a NEW object (never mutates).
 */
export const classifyAttribution = (attr: IAttribution): IAttribution => {
  if (attr.utmSource && attr.utmSource.length > 0) return attr

  if (attr.gclid) {
    return { ...attr, utmSource: 'google', utmMedium: 'cpc' }
  }
  if (attr.fbclid) {
    return { ...attr, utmSource: 'facebook', utmMedium: 'paid-social' }
  }
  if (attr.msclkid) {
    return { ...attr, utmSource: 'bing', utmMedium: 'cpc' }
  }

  const referrer = attr.utmReferrer
  if (!referrer) {
    return { ...attr, utmSource: '(direct)', utmMedium: '(none)' }
  }

  const host = parseHostname(referrer)
  if (!host) return { ...attr, utmSource: '(direct)', utmMedium: '(none)' }

  if (host.endsWith('spotme.mx')) {
    return { ...attr, utmSource: '(internal)', utmMedium: 'internal' }
  }

  for (const rule of REFERRER_RULES) {
    if (rule.match.test(host)) {
      return { ...attr, utmSource: rule.source, utmMedium: rule.medium }
    }
  }

  return { ...attr, utmSource: host, utmMedium: 'referral' }
}
